const MICDROP_URI = 'wss://micdrop.comprehendhealth.ai';
const CONNECT_TIMEOUT_MS = 10000;
const STREAM_STALL_MS = 25000;

/**
 * WebSocket channel for streaming AI chat tokens. Authenticates against the
 * MicDrop WebSocket API with a `chat_init` message (a dedicated chat socket --
 * never share a socket with streaming transcription), then receives
 * `{chat_token}` / `{done, response}` frames pushed by the backend's
 * /v1/conversation/chat/question/stream endpoint.
 *
 * Best-effort by design: connect() resolves null on any failure (timeout,
 * auth rejection, socket error) so the caller can fall back to the normal
 * non-streaming request. One instance per Chat component; the socket connects
 * lazily on first use and stays open for follow-up questions.
 */
export class ChatStream {
  constructor(apiClient) {
    this.apiClient = apiClient;
    this.websocket = null;
    this.connectionId = null;
    this.connectPromise = null;
    this.onToken = null; // (textChunk) => void
    this.onDone = null; // (fullResponse) => void
    this.cancelStreamWait = null;
  }

  // Resolves with the connectionId, or null if streaming is unavailable.
  connect() {
    if (this.connectionId) return Promise.resolve(this.connectionId);
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = new Promise((resolve) => {
      let settled = false;
      const settle = (value) => {
        if (!settled) {
          settled = true;
          resolve(value);
        }
      };
      // The backend never replies to a failed chat_init (no route responses),
      // so a timeout is the only signal that auth failed or the socket is dead.
      // Tear the socket down so the next send starts a fresh connect instead of
      // reusing a zombie socket with a cached null result.
      const timeout = setTimeout(() => {
        settle(null);
        this.close();
      }, CONNECT_TIMEOUT_MS);

      let ws;
      try {
        ws = new WebSocket(MICDROP_URI);
      } catch {
        clearTimeout(timeout);
        settle(null);
        return;
      }
      this.websocket = ws;

      ws.onopen = () => {
        const messageBody = {
          type: 'chat_init',
          sessionEmail: this.apiClient.sessionEmail,
          sessionKey: this.apiClient.sessionKey,
        };
        ws.send(
          JSON.stringify({
            action: 'sendmessage',
            message: JSON.stringify(messageBody),
          }),
        );
      };
      ws.onmessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message.message === 'chat_ready' && message.connectionId) {
          this.connectionId = message.connectionId;
          clearTimeout(timeout);
          settle(this.connectionId);
        } else if (message.chat_token && this.onToken) {
          this.onToken(message.chat_token);
        } else if (message.done && this.onDone) {
          this.onDone(message.response);
        } else if (message.message === 'Success') {
          // API Gateway route success frame; chat_ready is the usable handshake.
        }
      };
      ws.onerror = () => {
        clearTimeout(timeout);
        settle(null);
      };
      ws.onclose = () => {
        this.websocket = null;
        this.connectionId = null;
        this.connectPromise = null;
        clearTimeout(timeout);
        settle(null);
      };
    });
    return this.connectPromise;
  }

  close() {
    this.clearHandlers();
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.connectionId = null;
    this.connectPromise = null;
  }

  clearHandlers() {
    if (this.cancelStreamWait) {
      this.cancelStreamWait();
      this.cancelStreamWait = null;
    }
    this.onToken = null;
    this.onDone = null;
  }

  waitForStreamDone(streamState) {
    if (streamState.doneResponse) {
      return Promise.resolve(streamState.doneResponse);
    }

    return new Promise((resolve) => {
      let watchdog = null;
      const cancel = () => finish(null);
      const finish = (response) => {
        clearInterval(watchdog);
        if (this.cancelStreamWait === cancel) {
          this.cancelStreamWait = null;
        }
        streamState.resolveDone = null;
        resolve(response);
      };

      streamState.resolveDone = finish;
      this.cancelStreamWait = cancel;
      watchdog = setInterval(() => {
        if (Date.now() - streamState.lastTokenAt > STREAM_STALL_MS) {
          finish(null);
        }
      }, 1000);
    });
  }

  async askQuestion(conversationId, text, onToken, onDone) {
    let connectionId = null;
    try {
      connectionId = await this.connect();
    } catch {
      connectionId = null;
    }

    if (!connectionId) {
      return this.apiClient.askChatQuestion(conversationId, text);
    }

    const streamState = {
      receivedToken: false,
      lastTokenAt: 0,
      doneResponse: null,
      resolveDone: null,
    };
    this.onToken = (chunk) => {
      streamState.receivedToken = true;
      streamState.lastTokenAt = Date.now();
      if (onToken) onToken(chunk);
    };
    this.onDone = (response) => {
      streamState.doneResponse = response;
      if (onDone) onDone(response);
      if (streamState.resolveDone) streamState.resolveDone(response);
    };

    let response;
    try {
      response = await this.apiClient.askChatQuestionStream(
        conversationId,
        text,
        connectionId,
      );
    } catch (err) {
      if (
        !streamState.receivedToken ||
        !this.apiClient.isTimeoutError(err)
      ) {
        throw err;
      }

      // API Gateway can time out the HTTP request while the WebSocket is still
      // delivering the response. Keep the active stream unless it stalls.
      response = await this.waitForStreamDone(streamState);
      if (!response) throw err;
      return { response };
    }

    if (response && response.error === 'Invalid streaming connection') {
      this.close();
      return this.apiClient.askChatQuestion(conversationId, text);
    }

    return response;
  }
}

export default ChatStream;
